import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaPlus, FaTimes, FaEdit, FaTrash, FaCalendarAlt, FaClock } from 'react-icons/fa';
import {
  getDoctorSlots,
  createDoctorSlot,
  deactivateDoctorSlot
} from '../services/doctorService';
import { useAuth } from '../auth/AuthContext';
import NotificationToast from '../components/common/NotificationToast';
import { DOCTOR_LIST_PATH, DOCTOR_LIST_URL_KEY, resolveListBackTarget } from '../utils/subscriberHelpers';

const WEEKDAYS = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

const DoctorSlotManagement = () => {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // The list page/filters this doctor was opened from, so Back lands on the same rows.
  const backToList = resolveListBackTarget(location.state, DOCTOR_LIST_PATH, DOCTOR_LIST_URL_KEY);
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [doctor, setDoctor] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [notificationMounted, setNotificationMounted] = useState(false);

  const [showSlotModal, setShowSlotModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [slotFormData, setSlotFormData] = useState({
    weekdays: [],
    start_time: '',
    end_time: '',
    active: true,
  });

  const [editSlotFormData, setEditSlotFormData] = useState({
    weekday: null,
    start_time: '',
    end_time: '',
  });

  const [slotErrors, setSlotErrors] = useState({});
  const [editSlotErrors, setEditSlotErrors] = useState({});

  const currentRole = (() => {
    const authRole = localStorage.getItem('authRole');
    const roleMapping = {
      wallet_superadmin: 'SuperAdmin',
      wallet_finance: 'Finance',
      wallet_ops: 'Ops',
      wallet_support: 'Support',
    };
    return roleMapping[authRole] || null;
  })();

  // Check if user can create slots (Finance and SuperAdmin only, not Ops)
  const canCreateSlot = currentRole === 'Finance' || currentRole === 'SuperAdmin';

  useEffect(() => {
    setNotificationMounted(true);
    return () => setNotificationMounted(false);
  }, []);

  useEffect(() => {
    if (!notification) return;

    const timer = setTimeout(() => {
      setNotification(null);
    }, 1000); // ⏱️ hide after 1 seconds

    return () => clearTimeout(timer);
  }, [notification]);


  useEffect(() => {
    if (!isAuthenticated || authLoading || !doctorId) return;

    const load = async () => {
      setLoading(true);
      try {
        const slotRes = await getDoctorSlots(currentRole, doctorId);

        const apiMsg = slotRes?.msg;

        setDoctor(apiMsg?.doctor || null);
        setSlots(apiMsg?.availability || []);

      } catch (error) {
        console.error('Error loading doctor data:', error);
        setNotification({
          type: 'error',
          message: 'Failed to load doctor information'
        });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [doctorId, currentRole, isAuthenticated, authLoading]);

  const formatTimeToAMPM = (time) => {
    if (!time) return 'N/A';
    const [hourStr, minuteStr] = time.split(':');
    let hour = parseInt(hourStr, 10);
    const minute = minuteStr;

    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;

    return `${hour}:${minute} ${ampm}`;
  };

  // Helper function to break time range into 15-minute slots
  const breakTimeRangeIntoSlots = (startTime, endTime) => {
    const slots = [];
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    let currentHour = startHour;
    let currentMin = startMin;
    
    while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
      const slotStart = `${String(currentHour).padStart(2, '0')}:${String(currentMin).padStart(2, '0')}`;
      
      // Add 15 minutes
      currentMin += 15;
      if (currentMin >= 60) {
        currentMin -= 60;
        currentHour += 1;
      }
      
      // Check if we've exceeded end time
      if (currentHour > endHour || (currentHour === endHour && currentMin > endMin)) {
        break;
      }
      
      const slotEnd = `${String(currentHour).padStart(2, '0')}:${String(currentMin).padStart(2, '0')}`;
      
      slots.push({
        start_time: slotStart,
        end_time: slotEnd
      });
    }
    
    return slots;
  };

  // Group consecutive slots into time ranges
  const groupSlotsIntoRanges = (slots) => {
    if (!slots || slots.length === 0) return [];

    const sorted = [...slots].sort((a, b) => a.start_time.localeCompare(b.start_time));
    const ranges = [];
    let currentRange = {
      start_time: sorted[0].start_time,
      end_time: sorted[0].end_time,
      slot_ids: [sorted[0].id]
    };

    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const curr = sorted[i];

      // If current slot starts exactly when previous ends, merge them
      if (prev.end_time === curr.start_time) {
        currentRange.end_time = curr.end_time;
        currentRange.slot_ids.push(curr.id);
      } else {
        // Save current range and start a new one
        ranges.push(currentRange);
        currentRange = {
          start_time: curr.start_time,
          end_time: curr.end_time,
          slot_ids: [curr.id]
        };
      }
    }

    ranges.push(currentRange);
    return ranges;
  };

  // const validateForm = () => {
  //   const e = {};

  //   const MIN_TIME = '09:00';
  //   const MAX_TIME = '18:00';

  //   if (!slotFormData.weekdays.length) {
  //     e.weekdays = 'Select at least one weekday';
  //   }

  //   if (!slotFormData.start_time) {
  //     e.start_time = 'Start time is required';
  //   } else if (
  //     slotFormData.start_time < MIN_TIME ||
  //     slotFormData.start_time > MAX_TIME
  //   ) {
  //     e.start_time = 'Start time must be between 09:00 and 18:00';
  //   }

  //   if (!slotFormData.end_time) {
  //     e.end_time = 'End time is required';
  //   } else if (
  //     slotFormData.end_time < MIN_TIME ||
  //     slotFormData.end_time > MAX_TIME
  //   ) {
  //     e.end_time = 'End time must be between 09:00 and 18:00';
  //   }

  //   if (
  //     slotFormData.start_time &&
  //     slotFormData.end_time &&
  //     slotFormData.start_time >= slotFormData.end_time
  //   ) {
  //     e.end_time = 'End time must be after start time';
  //   }

  //   setSlotErrors(e);
  //   return Object.keys(e).length === 0;
  // };
  const validateForm = () => {
  const e = {};

  // HH:MM (24-hour format)
  const isValidTimeFormat = (time) => /^([01]\d|2[0-3]):([0-5]\d)$/.test(time);

  if (!slotFormData.weekdays.length) {
    e.weekdays = 'Select at least one weekday';
  }

  if (!slotFormData.start_time) {
    e.start_time = 'Start time is required';
  } else if (!isValidTimeFormat(slotFormData.start_time)) {
    e.start_time = 'Invalid time format (HH:MM)';
  }

  if (!slotFormData.end_time) {
    e.end_time = 'End time is required';
  } else if (!isValidTimeFormat(slotFormData.end_time)) {
    e.end_time = 'Invalid time format (HH:MM)';
  }

  if (
    slotFormData.start_time &&
    slotFormData.end_time &&
    isValidTimeFormat(slotFormData.start_time) &&
    isValidTimeFormat(slotFormData.end_time) &&
    slotFormData.start_time >= slotFormData.end_time
  ) {
    e.end_time = 'End time must be after start time';
  }

  setSlotErrors(e);
  return Object.keys(e).length === 0;
};

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitting(true);

    try {
      // Break time range into 15-minute slots for each weekday
      const timeSlots = breakTimeRangeIntoSlots(slotFormData.start_time, slotFormData.end_time);
      
      const payload = slotFormData.weekdays.flatMap(day => 
        timeSlots.map(slot => ({
          weekday: day,
          start_time: slot.start_time,
          end_time: slot.end_time,
          active: Boolean(slotFormData.active),
        }))
      );

      await createDoctorSlot(currentRole, doctorId, payload);
      setNotification({
        type: 'success',
        message: 'Slots added successfully!'
      });

      // Reload slots
      const refreshed = await getDoctorSlots(currentRole, doctorId);
      const apiMsg = refreshed?.msg;
      setSlots(apiMsg?.availability || []);

      setShowSlotModal(false);
      setSlotFormData({ weekdays: [], start_time: '', end_time: '', active: true });
      setEditingSlot(null);
    } catch (error) {
      console.error('Error saving slot:', error);
      setNotification({
        type: 'error',
        message: error.message || 'Failed to save slot. Please try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentRole) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
          <button 
            onClick={() => navigate(backToList)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Doctors List
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button 
            onClick={() => navigate(backToList)} 
            className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
          >
            ← Back to Doctors List
          </button>
        </div>

        {/* Doctor Info Card */}
        {doctor && (
          <div className="bg-white rounded-md shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {doctor.first_name} {doctor.last_name}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
              <div>
                <span className="font-medium">Specialization:</span>{" "}
                {Array.isArray(doctor.specializations)
                  ? doctor.specializations.join(", ")
                  : Array.isArray(doctor.specialization)
                  ? doctor.specialization.join(", ")
                  : doctor.specializations || doctor.specialization || "N/A"}
              </div>
              <div>
                <span className="font-medium">Qualification:</span>{' '}
                {Array.isArray(doctor.qualifications) ? doctor.qualifications.join(', ') : doctor.qualifications || 'N/A'}
              </div>
              <div>
                <span className="font-medium">Mobile Number:</span> {doctor?.mobile || 'N/A'}
              </div>
            </div>
          </div>
        )}

        {/* Slots Management */}
        <div className="bg-white rounded-md shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <FaCalendarAlt className="w-5 h-5 mr-2" />
                <span>Slot & Availability Management</span>
              </h2>
              {canCreateSlot && (
                <button
                  onClick={() => {
                    setEditingSlot(null);
                    setSlotFormData({ weekdays: [], start_time: '', end_time: '', active: true });
                    setSlotErrors({});
                    setShowSlotModal(true);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 flex items-center gap-2 transition-colors"
                >
                  <FaPlus className="w-3 h-3" />
                  Add Slot
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            {!Array.isArray(slots) || slots.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                <FaCalendarAlt className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium mb-2">No slots available</p>
                {canCreateSlot && (
                  <p className="text-sm">Click "Add Slot" to create availability slots for this doctor.</p>
                )}
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Time</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Time</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    {canCreateSlot && (
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {slots.map(day => {
                    const ranges = groupSlotsIntoRanges(day.slots || []);
                    
                    if (ranges.length === 0) {
                      return (
                        <tr key={day.weekday} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {day.weekday_name ||
                              WEEKDAYS.find(d => d.value === day.weekday)?.label ||
                              'N/A'}
                          </td>
                          <td colSpan={canCreateSlot ? "4" : "3"} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                            No slots available
                          </td>
                        </tr>
                      );
                    }

                    return ranges.map((range, rangeIndex) => (
                      <tr key={`${day.weekday}-${rangeIndex}`} className="hover:bg-gray-50">
                        {/* Weekday - only show on first range of the day */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {rangeIndex === 0 ? (
                            day.weekday_name ||
                            WEEKDAYS.find(d => d.value === day.weekday)?.label ||
                            'N/A'
                          ) : null}
                        </td>

                        {/* Start Time */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center gap-2">
                            <FaClock className="w-3 h-3" />
                            {formatTimeToAMPM(range.start_time)}
                          </div>
                        </td>

                        {/* End Time */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatTimeToAMPM(range.end_time)}
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Active
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                          {canCreateSlot && (
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingSlot({ ...range, weekday: day.weekday });
                                  setEditSlotFormData({
                                    weekday: day.weekday,
                                    start_time: range.start_time || '',
                                    end_time: range.end_time || '',
                                  });
                                  setEditSlotErrors({});
                                  setShowEditModal(true);
                                }}
                                className="text-blue-600 hover:text-blue-900 transition-colors"
                                title="Edit time range"
                              >
                                <FaEdit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Are you sure you want to delete the time range ${formatTimeToAMPM(range.start_time)} - ${formatTimeToAMPM(range.end_time)}?`)) {
                                    try {
                                      // Delete all slots in this range
                                      await Promise.all(
                                        range.slot_ids.map(id =>
                                          deactivateDoctorSlot(currentRole, doctorId, id)
                                        )
                                      );
                                      setNotification({
                                        type: 'success',
                                        message: 'Time range deleted successfully!'
                                      });
                                      // Reload slots
                                      const refreshed = await getDoctorSlots(currentRole, doctorId);
                                      const apiMsg = refreshed?.msg;
                                      setSlots(apiMsg?.availability || []);
                                    } catch (error) {
                                      console.error('Error deleting slots:', error);
                                      setNotification({
                                        type: 'error',
                                        message: error.message || 'Failed to delete time range. Please try again.'
                                      });
                                    }
                                  }
                                }}
                                className="text-red-600 hover:text-red-900 transition-colors"
                                title="Delete time range"
                              >
                                <FaTrash className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Add Slot Modal */}
        {showSlotModal && (
          <div className="fixed top-0 left-0 w-screen h-screen bg-black/80 z-[9999] m-0 p-0 flex items-center justify-center overflow-y-auto">
            <div className="bg-white rounded-lg border border-gray-200 w-full max-w-md shadow-xl m-6">
              <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
                <h2 className="text-lg font-semibold text-gray-900">
                  Add Slot
                </h2>
                <button
                  onClick={() => {
                    if (!submitting) {
                      setShowSlotModal(false);
                      setSlotFormData({ weekdays: [], start_time: '', end_time: '', active: true });
                      setSlotErrors({});
                    }
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={submitting}
                >
                  <FaTimes />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Start Time and End Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Start Time *
                    </label>
                    {/* <input
                      type="time"
                      min="09:00"
                      max="18:00"
                      className={`w-full border rounded-md px-2 py-1.5 text-sm ${
                        slotErrors.start_time ? 'border-red-500' : 'border-gray-300'
                      }`}
                      value={slotFormData.start_time}
                      onChange={e => {
                        setSlotFormData({ ...slotFormData, start_time: e.target.value });
                        setSlotErrors(prev => ({ ...prev, start_time: '' }));
                      }}
                      disabled={submitting}
                    /> */}
                    <input
  type="text"
  placeholder="HH:MM (e.g. 09:30)"
  className={`w-full border rounded-md px-2 py-1.5 text-sm ${
    slotErrors.start_time ? 'border-red-500' : 'border-gray-300'
  }`}
  value={slotFormData.start_time}
  onChange={e => {
    setSlotFormData({ ...slotFormData, start_time: e.target.value });
    setSlotErrors(prev => ({ ...prev, start_time: '' }));
  }}
/>

                    {slotErrors.start_time && (
                      <p className="text-xs text-red-500 mt-1">{slotErrors.start_time}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      End Time *
                    </label>
                    <input
                      type="text"
                      placeholder="HH:MM (e.g. 17:30)"
                      className={`w-full border rounded-md px-2 py-1.5 text-sm ${
                        slotErrors.end_time ? 'border-red-500' : 'border-gray-300'
                      }`}
                      value={slotFormData.end_time}
                      onChange={e => {
                        setSlotFormData({ ...slotFormData, end_time: e.target.value });
                        setSlotErrors(prev => ({ ...prev, end_time: '' }));
                      }}
                      disabled={submitting}
                    />
                    {slotErrors.end_time && (
                      <p className="text-xs text-red-500 mt-1">{slotErrors.end_time}</p>
                    )}
                  </div>
                </div>

                {/* Weekdays Checkboxes */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Select Weekdays *
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {WEEKDAYS.map(day => (
                      <label key={day.value} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={slotFormData.weekdays.includes(day.value)}
                          onChange={e => {
                            const checked = e.target.checked;
                            setSlotFormData(prev => ({
                              ...prev,
                              weekdays: checked
                                ? [...prev.weekdays, day.value]
                                : prev.weekdays.filter(d => d !== day.value),
                            }));
                            setSlotErrors(prev => ({ ...prev, weekdays: '' }));
                          }}
                          className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                        />
                        <span>{day.label}</span>
                      </label>
                    ))}
                  </div>
                  {slotErrors.weekdays && (
                    <p className="text-xs text-red-500 mt-1">{slotErrors.weekdays}</p>
                  )}
                </div>

                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={slotFormData.active}
                      onChange={e => setSlotFormData({ ...slotFormData, active: e.target.checked })}
                      disabled={submitting}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-2 pt-4 border-t">
                  <button
                    onClick={() => {
                      if (!submitting) {
                        setShowSlotModal(false);
                        setSlotFormData({ weekdays: [], start_time: '', end_time: '', active: true });
                        setSlotErrors({});
                      }
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:text-white hover:bg-blue-600 hover:border-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={submitting}
                  >
                    {submitting ? 'Saving...' : 'Add Slot'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Slot Modal */}
        {showEditModal && editingSlot && (
          <div className="fixed top-0 left-0 w-screen h-screen bg-black/80 z-[9999] m-0 p-0 flex items-center justify-center overflow-y-auto">
            <div className="bg-white rounded-lg border border-gray-200 w-full max-w-md shadow-xl m-6">
              <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
                <h2 className="text-lg font-semibold text-gray-900">
                  Edit Slot
                </h2>
                <button
                  onClick={() => {
                    if (!submitting) {
                      setShowEditModal(false);
                      setEditingSlot(null);
                      setEditSlotFormData({ weekday: null, start_time: '', end_time: '' });
                      setEditSlotErrors({});
                    }
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={submitting}
                >
                  <FaTimes />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Weekday */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Weekday *
                  </label>
                  <select
                    className={`w-full border rounded-md px-2 py-1.5 text-sm bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      editSlotErrors.weekday ? 'border-red-500' : 'border-gray-300'
                    }`}
                    value={editSlotFormData.weekday ?? ''}
                    onChange={e => {
                      setEditSlotFormData({ ...editSlotFormData, weekday: Number(e.target.value) });
                      if (editSlotErrors.weekday) {
                        setEditSlotErrors(prev => ({ ...prev, weekday: '' }));
                      }
                    }}
                    disabled={submitting}
                  >
                    <option value="">Select Day</option>
                    {WEEKDAYS.map(d => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                  {editSlotErrors.weekday && (
                    <p className="text-xs text-red-500 mt-1">{editSlotErrors.weekday}</p>
                  )}
                </div>

                {/* Start Time and End Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Start Time *
                    </label>
                    <input
                      type="time"
                      min="09:00"
                      max="18:00"
                      className={`w-full border rounded-md px-2 py-1.5 text-sm ${
                        editSlotErrors.start_time ? 'border-red-500' : 'border-gray-300'
                      }`}
                      value={editSlotFormData.start_time}
                      onChange={e => {
                        setEditSlotFormData({ ...editSlotFormData, start_time: e.target.value });
                        setEditSlotErrors(prev => ({ ...prev, start_time: '' }));
                      }}
                      disabled={submitting}
                    />
                    {editSlotErrors.start_time && (
                      <p className="text-xs text-red-500 mt-1">{editSlotErrors.start_time}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      End Time *
                    </label>
                    <input
                      type="time"
                      min="09:00"
                      max="18:00"
                      className={`w-full border rounded-md px-2 py-1.5 text-sm ${
                        editSlotErrors.end_time ? 'border-red-500' : 'border-gray-300'
                      }`}
                      value={editSlotFormData.end_time}
                      onChange={e => {
                        setEditSlotFormData({ ...editSlotFormData, end_time: e.target.value });
                        setEditSlotErrors(prev => ({ ...prev, end_time: '' }));
                      }}
                      disabled={submitting}
                    />
                    {editSlotErrors.end_time && (
                      <p className="text-xs text-red-500 mt-1">{editSlotErrors.end_time}</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-2 pt-4 border-t">
                  <button
                    onClick={() => {
                      if (!submitting) {
                        setShowEditModal(false);
                        setEditingSlot(null);
                        setEditSlotFormData({ weekday: null, start_time: '', end_time: '' });
                        setEditSlotErrors({});
                      }
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:text-white hover:bg-blue-600 hover:border-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      // Validate form
                      const errors = {};
                      if (editSlotFormData.weekday === null || editSlotFormData.weekday === '') {
                        errors.weekday = 'Weekday is required';
                      }
                      if (!editSlotFormData.start_time) {
                        errors.start_time = 'Start time is required';
                      }
                      if (!editSlotFormData.end_time) {
                        errors.end_time = 'End time is required';
                      }
                      if (editSlotFormData.start_time && editSlotFormData.end_time && 
                          editSlotFormData.start_time >= editSlotFormData.end_time) {
                        errors.end_time = 'End time must be after start time';
                      }

                      if (Object.keys(errors).length > 0) {
                        setEditSlotErrors(errors);
                        return;
                      }

                      setSubmitting(true);
                      try {
                        // Delete all existing slots in the range
                        if (editingSlot && editingSlot.slot_ids) {
                          await Promise.all(
                            editingSlot.slot_ids.map(id =>
                              deactivateDoctorSlot(currentRole, doctorId, id)
                            )
                          );
                        }

                        // Break new time range into 15-minute slots
                        const timeSlots = breakTimeRangeIntoSlots(editSlotFormData.start_time, editSlotFormData.end_time);
                        
                        // Create new slots
                        const payload = timeSlots.map(slot => ({
                          weekday: Number(editSlotFormData.weekday),
                          start_time: slot.start_time,
                          end_time: slot.end_time,
                          active: true,
                        }));

                        await createDoctorSlot(currentRole, doctorId, payload);
                        
                        setNotification({
                          type: 'success',
                          message: 'Time range updated successfully!'
                        });

                        // Reload slots
                        const refreshed = await getDoctorSlots(currentRole, doctorId);
                        const apiMsg = refreshed?.msg;
                        setSlots(apiMsg?.availability || []);

                        setShowEditModal(false);
                        setEditingSlot(null);
                        setEditSlotFormData({ weekday: null, start_time: '', end_time: '' });
                        setEditSlotErrors({});
                      } catch (error) {
                        console.error('Error updating slots:', error);
                        setNotification({
                          type: 'error',
                          message: error.message || 'Failed to update time range. Please try again.'
                        });
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={submitting}
                  >
                    {submitting ? 'Updating...' : 'Update Slot'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {notification && (
          <NotificationToast
            mounted={notificationMounted}
            notification={notification}
            onClose={() => setNotification(null)}
          />
        )}
      </div>
    </div>
  );
};

export default DoctorSlotManagement;
